import type { ReactNode } from "react";

import { curveStepAfter } from "d3-shape";
import { compact } from "lodash";

import { Chart } from "@eisox/design-system";
import { barY, defineChart, group, lineY, stack, text } from "@tanstack/charts";
import { d3Curve } from "@tanstack/charts/d3/shape";
import { decorative } from "@tanstack/charts/mark/decorative";
import { ruleY } from "@tanstack/charts/rule";
import { scaleBand } from "@tanstack/charts/scales/band";
import { scaleLinear } from "@tanstack/charts/scales/linear";

import { m } from "~/paraglide/messages";

import { BASELOAD_COLOR } from "../consumption.constant";
import { formatAxisValue } from "../helpers/energy.helper";
import type { ChartRow, ChartSeries } from "../types/consumption.type";

const BAR_RADIUS = 2;
const IN_PROGRESS_MIX = 50;
const LEVEL_DASH = "6 4";
const BASELOAD_DASH = "4 4";
const BASELOAD_WIDTH = 2;
const BASELOAD_OPACITY = 0.7;
const BASELOAD_LABEL_OFFSET = -10;
const BASELOAD_LABEL_ID = "baseload-label";

interface ChartPoint {
  start: string;
  series: string;
  value: number;
  inProgress: boolean;
}

interface ConsumptionChartProps {
  rows: ChartRow[] | ((hiddenSeries: readonly string[]) => ChartRow[]);
  series: ChartSeries[];
  unit?: string;
  baseload?: number;
  formatTick: (start: string) => string;
  formatValue: (value: number) => string;
  formatTooltipTitle: (start: string) => string;
  renderTooltip?: (start: string) => ReactNode;
  onPointSelect?: (start: string, series: string) => void;
}

const ConsumptionChart: React.FC<ConsumptionChartProps> = ({
  rows,
  series,
  unit,
  baseload,
  formatTick,
  formatValue,
  formatTooltipTitle,
  renderTooltip,
  onPointSelect,
}) => {
  const seriesOf = (key: string) => series.find(candidate => candidate.key === key);
  const fillOf = ({ series: key, inProgress }: ChartPoint) => {
    const { color = BASELOAD_COLOR, fadesInProgress } = seriesOf(key) ?? {};
    return inProgress && fadesInProgress ? `color-mix(in srgb, ${color} ${IN_PROGRESS_MIX}%, transparent)` : color;
  };

  const definitionOf = (hiddenSeries: readonly string[]) => {
    const shown = series.filter(({ key }) => !hiddenSeries.includes(key));
    const shownRows = typeof rows === "function" ? rows(hiddenSeries) : rows;
    const lastStart = shownRows.at(-1)?.start;
    const pointsOf = (kept: ChartSeries[]): ChartPoint[] =>
      shownRows
        .flatMap(row =>
          kept.map(({ key }) => ({ start: row.start, series: key, value: row[key], inProgress: !!row.inProgress })),
        )
        .filter((point): point is ChartPoint => typeof point.value === "number");
    return defineChart({
      marks: [
        barY(pointsOf(shown.filter(({ kind }) => kind === "bar")), {
          x: "start",
          y: "value",
          color: "series",
          fill: fillOf,
          radius: BAR_RADIUS,
          layout: series.some(({ stackId }) => stackId) ? stack({ order: series.map(({ key }) => key) }) : group(),
        }),
        ...shown
          .filter(({ kind }) => kind === "level")
          .map(level =>
            lineY(pointsOf([level]), {
              x: "start",
              y: "value",
              color: "series",
              strokeDasharray: LEVEL_DASH,
              curve: d3Curve(curveStepAfter),
            }),
          ),
        ...(baseload === undefined || lastStart === undefined
          ? []
          : [
              ruleY([baseload], {
                stroke: BASELOAD_COLOR,
                strokeOpacity: BASELOAD_OPACITY,
                strokeWidth: BASELOAD_WIDTH,
                strokeDasharray: BASELOAD_DASH,
              }),
              decorative(
                text([{ start: lastStart, series: m.consumption_baseload(), value: baseload, inProgress: false }], {
                  id: BASELOAD_LABEL_ID,
                  x: "start",
                  y: "value",
                  text: "series",
                  anchor: "end",
                  dy: BASELOAD_LABEL_OFFSET,
                  fill: BASELOAD_COLOR,
                  fontWeight: 500,
                }),
              ),
            ]),
      ],
      color: { domain: series.map(({ key }) => key), range: series.map(({ color }) => color) },
      scales: {
        x: {
          scale: () => scaleBand().padding(0.2),
          axis: { ...Chart.axis, ticks: { ...Chart.axis.ticks, format: (start: string) => formatTick(start) } },
        },
        y: {
          scale: scaleLinear,
          nice: true,
          grid: Chart.grid,
          axis: {
            ...Chart.axis,
            label: unit,
            ticks: {
              ...Chart.axis.ticks,
              format: formatAxisValue,
            },
          },
        },
      },
      focus: "group-x",
      maxFocusDistance: 0,
      tooltip: {
        ...Chart.tooltip,
        content: points => ({
          title: points[0] && formatTooltipTitle(points[0].datum.start),
          rows: points.map(({ datum, color }) => ({
            label: seriesOf(datum.series)?.label ?? "",
            value: formatValue(datum.value),
            color,
          })),
        }),
      },
    });
  };

  return (
    <Chart.Root<ChartPoint, string, number>
      key={series.map(({ key }) => key).join()}
      definition={definitionOf}
      defaultHiddenSeries={compact(series.map(({ replaces }) => replaces))}
      ariaLabel={m.consumption()}
      className="md:min-h-80 min-h-96 flex-1 [--ts-chart-tooltip-max-width:none] [&_[data-ts-key^=baseload-label]]:stroke-primary-white/70 [&_[data-ts-key^=baseload-label]]:stroke-3 [&_[data-ts-key^=baseload-label]]:opacity-70 [&_[data-ts-key^=baseload-label]]:[paint-order:stroke]"
      onSelect={onPointSelect && (point => point && onPointSelect(point.datum.start, point.datum.series))}
      renderTooltipBody={
        renderTooltip && (({ points, defaultBody }) => (points[0] ? renderTooltip(points[0].datum.start) : defaultBody))
      }
    >
      <Chart.Legend label={key => seriesOf(key)?.label ?? key} />
    </Chart.Root>
  );
};

ConsumptionChart.displayName = "ConsumptionChart";

export { ConsumptionChart };
